/**
 * Checks on a chart scene as the renderer shows it, read through luminary's composition
 * introspection (its GSAP build and layout stylesheet in place).
 *
 * Two kinds, used differently:
 * - Validity: a scene that never animates or renders nothing. Disqualifying and deterministic, so
 *   the chart subagent is told and gets one retry.
 * - Layout and reading load: text that collides, hides under a fill, is cut off, or runs off the
 *   frame, and how much there is to read. Each detection was checked by eye against real designs,
 *   but none is fed back to a model: they are recorded so their rate can decide whether a fix round
 *   is worth adding.
 */

import {
    compositionRendersNothing,
    isOneFlatColour,
} from 'js/src/agent/html-design/lint/blankCompositionRule';

/** The part of luminary's `CompositionIntrospection` these checks read. */
export interface ChartComposition {
    readonly document: Document;
    readonly timeline: unknown;
    /** Older luminary poses in place and returns nothing; the posed `document` is then current. */
    seek(progress: number): Document | Promise<Document | void> | void;
    rasterize(scale: number): Promise<HTMLCanvasElement>;
}

export interface FrameSize {
    width: number;
    height: number;
}

export type Box = [x: number, y: number, width: number, height: number];

export interface TextRun {
    /** The whole text of its text node, which every line of the node carries. */
    text: string;
    /** Which line of its text node the box is, so a node's words are counted once. */
    line: number;
    element: Element;
    fontSize: number;
    box: Box;
}

export interface LayoutFindings {
    overlaps: Array<{ a: string; b: string }>;
    covered: Array<{ text: string; by: string }>;
    clipped: string[];
    offFrame: string[];
    /** Readable words on screen at the end of the scene. */
    words: number;
    /** Figures a viewer is asked to read: numbers at a readable size that aren't just a period. */
    figures: string[];
}

export interface ChartSceneCheck {
    /** Why the scene can't be shown, for the designer to fix; null when it runs. */
    problem: string | null;
    /** Null when the scene couldn't be laid out to measure. */
    layout: LayoutFindings | null;
}

// A line box includes leading, so tightly set lines of large type would read as colliding where
// no ink touches; glyphs fill about this much of the font size.
const INK_HEIGHT_EM = 0.72;
// Text collides when the glyph boxes cross deeply on both axes. A share of the smaller box would
// miss a long title whose last letters sit under a big figure.
const OVERLAP_MIN_HEIGHT_SHARE = 0.5;
const OVERLAP_MIN_WIDTH_EM = 0.5;
const OVERLAP_MIN_AREA = 120;
const EDGE_MARGIN = 2;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const SVG_SHAPES = new Set(['rect', 'path', 'circle', 'ellipse', 'polygon', 'polyline']);
const READABLE_MIN_PX = 18;
const FIGURE_MIN_PX = 22;
// Digits that only name a period (a year, Q1, H2, FY25) don't make text a figure, e.g. a title.
const PERIOD = /\b(?:FY ?\d{2,4}|[QH]\d|(?:19|20)\d{2})\b/gi;
// The final pose: the end of the scene, just inside it.
const END_PROGRESS = 0.995;

export const NO_TIMELINE_PROBLEM =
    "The scene never registered its timeline, so nothing in it can play. The script must build the timeline and end with `window.__timelines = { main: tl }` at the top level (not inside a function, where `tl` isn't visible), and nothing before that line may throw.";

export const BLANK_FRAME_PROBLEM = `The scene renders as one flat colour at its end: nothing on it is drawn. ${compositionRendersNothing.guidance}`;

export async function checkChartScene(
    composition: ChartComposition,
    frame: FrameSize,
): Promise<ChartSceneCheck> {
    if (!composition.timeline) {
        return { problem: NO_TIMELINE_PROBLEM, layout: null };
    }
    const document = (await composition.seek(END_PROGRESS)) ?? composition.document;
    const canvas = await composition.rasterize(0.25);
    if (isBlankCanvas(canvas)) {
        return { problem: BLANK_FRAME_PROBLEM, layout: null };
    }
    return { problem: null, layout: measureLayout(document, frame) };
}

export function measureLayout(document: Document, frame: FrameSize): LayoutFindings {
    const runs = visibleTextRuns(document);
    return {
        overlaps: findOverlaps(runs),
        covered: findCoveredText(runs, document),
        clipped: findClippedText(runs),
        offFrame: findOffFrameText(runs, frame),
        words: countReadableWords(runs),
        figures: findFigures(runs),
    };
}

/** Each visible line of text, boxed to roughly its glyphs rather than its line box. */
export function visibleTextRuns(document: Document): TextRun[] {
    const view = document.defaultView;
    if (!view || !document.body) {
        return [];
    }
    const runs: TextRun[] = [];
    const walker = document.createTreeWalker(document.body, view.NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const text = (node.textContent ?? '').replace(/\s+/g, ' ').trim();
        const element = node.parentElement;
        if (!text || !element || element.closest('script,style') || !isVisible(element, view)) {
            continue;
        }
        const fontSize = parseFloat(view.getComputedStyle(element).fontSize) || 16;
        const range = document.createRange();
        range.selectNodeContents(node);
        const rects = Array.from(range.getClientRects()).filter(
            (rect) => rect.width >= 1 && rect.height >= 1,
        );
        for (const [line, rect] of rects.entries()) {
            const inkHeight = Math.min(rect.height, INK_HEIGHT_EM * fontSize);
            runs.push({
                text,
                line,
                element,
                fontSize,
                box: [rect.left, rect.top + (rect.height - inkHeight) / 2, rect.width, inkHeight],
            });
        }
    }
    return runs;
}

function isVisible(element: Element, view: Window): boolean {
    let opacity = 1;
    for (
        let el: Element | null = element;
        el && el !== el.ownerDocument.documentElement;
        el = el.parentElement
    ) {
        const style = view.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }
        opacity *= parseFloat(style.opacity || '1');
    }
    return opacity >= 0.1;
}

function intersectionSize(a: Box, b: Box): [width: number, height: number] {
    return [
        Math.min(a[0] + a[2], b[0] + b[2]) - Math.max(a[0], b[0]),
        Math.min(a[1] + a[3], b[1] + b[3]) - Math.max(a[1], b[1]),
    ];
}

/**
 * Text on text: two different elements' glyph boxes crossing by at least half the shorter one's
 * height and half an em of the smaller type.
 */
export function findOverlaps(runs: TextRun[]): LayoutFindings['overlaps'] {
    const found: LayoutFindings['overlaps'] = [];
    for (let i = 0; i < runs.length; i++) {
        for (let j = i + 1; j < runs.length; j++) {
            const a = runs[i];
            const b = runs[j];
            if (a.element === b.element) {
                continue;
            }
            const [width, height] = intersectionSize(a.box, b.box);
            if (
                width * height >= OVERLAP_MIN_AREA &&
                height >= OVERLAP_MIN_HEIGHT_SHARE * Math.min(a.box[3], b.box[3]) &&
                width >= OVERLAP_MIN_WIDTH_EM * Math.min(a.fontSize, b.fontSize)
            ) {
                found.push({ a: label(a), b: label(b) });
            }
        }
    }
    return found;
}

/**
 * Text under something painted on top of it: hit-test points across each run's glyphs. Only fills
 * count as covering (a background or a filled SVG shape) — text on text is the overlap check's, at
 * glyph level, where a big headline's mostly-empty element box would read as covering its neighbours.
 */
export function findCoveredText(runs: TextRun[], document: Document): LayoutFindings['covered'] {
    const view = document.defaultView;
    if (!view || !document.head) {
        return [];
    }
    // Hit-testing skips elements that don't take pointer events, which can still paint over text.
    const unblock = document.createElement('style');
    unblock.textContent = '* { pointer-events: auto !important; }';
    document.head.appendChild(unblock);
    const found: LayoutFindings['covered'] = [];
    try {
        for (const run of runs) {
            const [x, y, width, height] = run.box;
            let painter: Element | null = null;
            let hits = 0;
            for (const fraction of [0.15, 0.5, 0.85]) {
                const top = document.elementFromPoint(x + fraction * width, y + height / 2);
                if (
                    !top ||
                    top === run.element ||
                    run.element.contains(top) ||
                    top.contains(run.element)
                ) {
                    continue;
                }
                // The hit is often a child of the covering box (its text); the fill can be on any
                // ancestor up to the one the covered text shares.
                for (
                    let el: Element | null = top;
                    el && !el.contains(run.element);
                    el = el.parentElement
                ) {
                    if (paintsSolidFill(el, view)) {
                        painter ??= el;
                        hits += 1;
                        break;
                    }
                }
            }
            if (hits >= 2 && painter) {
                found.push({ text: label(run), by: describeElement(painter) });
            }
        }
    } finally {
        unblock.remove();
    }
    return found;
}

function paintsSolidFill(element: Element, view: Window): boolean {
    const style = view.getComputedStyle(element);
    if (parseFloat(style.opacity || '1') < 0.5) {
        return false;
    }
    // Gradient text: the background paints only inside the glyphs.
    if (
        style.backgroundClip === 'text' ||
        style.getPropertyValue('-webkit-background-clip') === 'text'
    ) {
        return false;
    }
    if (
        backgroundAlpha(style.backgroundColor) >= 0.6 ||
        /linear-gradient/.test(style.backgroundImage)
    ) {
        return true;
    }
    if (element.namespaceURI === SVG_NAMESPACE && SVG_SHAPES.has(element.localName)) {
        return svgFillPaints(style.fill, style.fillOpacity);
    }
    return false;
}

/**
 * Whether an SVG shape's computed fill paints enough to hide text under it. Charts lay transparent
 * shapes over labels for layout or hit areas; a gradient (`url(...)`) fill counts as painting.
 */
export function svgFillPaints(fill: string, fillOpacity: string): boolean {
    if (fill === 'none' || fill === 'transparent') {
        return false;
    }
    const alpha = /rgba?\(/.test(fill) ? backgroundAlpha(fill) : 1;
    return alpha * parseFloat(fillOpacity || '1') >= 0.6;
}

export function backgroundAlpha(color: string): number {
    const parts = color.match(/rgba?\(([^)]+)\)/)?.[1].split(',');
    if (!parts) {
        return 0;
    }
    return parts.length === 4 ? parseFloat(parts[3]) : 1;
}

function describeElement(element: Element): string {
    const text = (element.textContent ?? '').trim().slice(0, 24);
    return (
        (element.id ? `#${element.id}` : element.tagName.toLowerCase()) + (text ? ` "${text}"` : '')
    );
}

/** Text cut off by an ancestor that clips its overflow: inside the frame, but partly hidden. */
export function findClippedText(runs: TextRun[]): string[] {
    const found: string[] = [];
    for (const run of runs) {
        const view = run.element.ownerDocument.defaultView;
        if (!view) {
            continue;
        }
        for (
            let el = run.element.parentElement;
            el && el.tagName !== 'BODY';
            el = el.parentElement
        ) {
            const style = view.getComputedStyle(el);
            if (!/hidden|clip/.test(style.overflowX + style.overflowY)) {
                continue;
            }
            if (overflowBeyond(run.box, el.getBoundingClientRect()) > EDGE_MARGIN) {
                found.push(label(run));
                break;
            }
        }
    }
    return [...new Set(found)];
}

export function overflowBeyond(
    box: Box,
    bounds: { left: number; top: number; right: number; bottom: number },
): number {
    const [x, y, width, height] = box;
    return Math.max(
        bounds.left - x,
        x + width - bounds.right,
        bounds.top - y,
        y + height - bounds.bottom,
    );
}

export function findOffFrameText(runs: TextRun[], frame: FrameSize): string[] {
    const bounds = { left: 0, top: 0, right: frame.width, bottom: frame.height };
    const offFrame = runs.filter((run) => overflowBeyond(run.box, bounds) > EDGE_MARGIN).map(label);
    return [...new Set(offFrame)];
}

export function countReadableWords(runs: TextRun[]): number {
    return runs
        .filter((run) => run.line === 0 && run.fontSize >= READABLE_MIN_PX)
        .reduce((total, run) => total + run.text.split(/\s+/).filter(Boolean).length, 0);
}

export function findFigures(runs: TextRun[]): string[] {
    const figures = runs
        .filter(
            (run) =>
                run.line === 0 &&
                run.fontSize >= FIGURE_MIN_PX &&
                /\d/.test(run.text.replace(PERIOD, '')),
        )
        .map(label);
    return [...new Set(figures)];
}

/** How a run is named in the recorded findings: its text, cut short. */
function label(run: TextRun): string {
    return run.text.slice(0, 40);
}

/** A frame with nothing drawn on it, by the same test the composition lint uses. */
export function isBlankCanvas(canvas: HTMLCanvasElement): boolean {
    const context = canvas.getContext('2d');
    if (!context || canvas.width === 0 || canvas.height === 0) {
        return true;
    }
    return isOneFlatColour(context.getImageData(0, 0, canvas.width, canvas.height).data);
}
